import React, { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ShoppingCart, ChevronLeft, ChevronRight, ZoomIn, Box } from "lucide-react";
import { ImageLightbox } from "./ImageLightbox";
import { resolveAsset } from "../../lib/assetUrl";

const ProductImageSlider = ({ images, name, onImageClick }) => {
  const [current, setCurrent] = useState(0);
  const allImages = images && images.length > 0 ? images : [];

  if (allImages.length === 0) return null;

  const prev = (e) => {
    e.stopPropagation();
    setCurrent((c) => (c === 0 ? allImages.length - 1 : c - 1));
  };
  const next = (e) => {
    e.stopPropagation();
    setCurrent((c) => (c === allImages.length - 1 ? 0 : c + 1));
  };

  return (
    <div
      className="relative overflow-hidden h-52 group/slider cursor-pointer"
      onClick={() => onImageClick(current)}
    >
      <img
        src={allImages[current]}
        alt={`${name} - ${current + 1}`}
        className="w-full h-full object-cover transition-opacity duration-300"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />

      {/* Zoom hint */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/slider:opacity-100 transition-all duration-300 scale-75 group-hover/slider:scale-100 shadow-lg">
          <ZoomIn className="w-5 h-5 text-slate-700" />
        </div>
      </div>

      {allImages.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300 shadow-sm hover:bg-white z-10"
          >
            <ChevronLeft className="w-4 h-4 text-slate-700" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300 shadow-sm hover:bg-white z-10"
          >
            <ChevronRight className="w-4 h-4 text-slate-700" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {allImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  idx === current ? "bg-white w-4" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ProductOptionPills = ({ options, selected, onSelect }) => {
  if (!options || options.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mb-3">
      {options.map((opt, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(idx)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 font-medium ${
            selected === idx
              ? "bg-sky-500 text-white border-sky-500 shadow-sm shadow-sky-200/50"
              : "bg-white text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-600"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const ProductCard = ({ product, index, isVisible, getProductLink, onImageClick }) => {
  const [selectedOption, setSelectedOption] = useState(0);
  const hasOptions = product.options && product.options.length > 0;

  const displayPrice = hasOptions
    ? product.options[selectedOption]?.price
    : product.price;

  const displayPriceNote = hasOptions ? "" : product.price_note;

  const productImages = (product.images && product.images.length > 0
    ? product.images
    : (product.image ? [product.image] : [])
  ).map(resolveAsset);

  const resolvedModel3d = resolveAsset(product.model_3d);

  const orderText = hasOptions
    ? `${product.name} (${product.options[selectedOption]?.label} - ${product.options[selectedOption]?.price})`
    : product.name;

  return (
    <Card
      className={`group overflow-hidden border border-sky-100/50 bg-white hover:shadow-xl hover:shadow-sky-100/40 hover:-translate-y-1.5 transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="relative">
        <ProductImageSlider
          images={productImages}
          name={product.name}
          onImageClick={(imgIndex) => onImageClick(productImages, imgIndex, resolvedModel3d || "")}
        />
        {product.tag && (
          <Badge className="absolute top-3 left-3 z-10 bg-sky-500/90 hover:bg-sky-500 text-white text-[11px] font-semibold backdrop-blur-sm border-0 pointer-events-none">
            {product.tag}
          </Badge>
        )}
        {resolvedModel3d && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-900/70 backdrop-blur-sm text-white text-[11px] font-bold pointer-events-none">
            <Box className="w-3 h-3" />
            3D
          </div>
        )}
      </div>
      <CardContent className="p-5">
        <h3 className="font-bold text-slate-900 text-lg leading-tight">{product.name}</h3>
        <p className="text-xs text-sky-600 font-semibold mb-2 tracking-wide">{product.subtitle}</p>
        <p className="text-sm text-slate-500 mb-3 line-clamp-2 leading-relaxed">{product.description}</p>

        <ProductOptionPills
          options={product.options}
          selected={selectedOption}
          onSelect={setSelectedOption}
        />

        <div className="flex items-end justify-between">
          <div>
            {displayPriceNote && <span className="text-[11px] text-slate-400 block">{displayPriceNote}</span>}
            <span
              key={displayPrice}
              className={`font-bold inline-block animate-price-pop ${
                displayPriceNote || hasOptions ? "text-xl text-slate-900" : "text-sm text-sky-600"
              }`}
            >
              {displayPrice}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => window.open(getProductLink(orderText), "_blank")}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full text-xs h-9 px-4 shadow-sm shadow-sky-200/50 hover:shadow-md transition-all duration-300"
          >
            <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
            Order
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const ProductCatalogLive = ({ products = [], siteContent }) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], startIndex: 0, model3d: "" });
  const whatsappNumber = siteContent?.whatsapp_number || "919056382775";

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.05 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [products.length]);

  const getProductLink = (productName) => {
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      `Hi Frostyware 3D! I'm interested in: ${productName}`
    )}`;
  };

  const openLightbox = (images, startIndex, model3d) => {
    setLightbox({ open: true, images, startIndex, model3d: model3d || "" });
  };

  if (products.length === 0) return null;

  return (
    <>
      <section id="products" ref={sectionRef} className="py-24 bg-slate-50/50 relative">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.015)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="text-sm font-semibold text-sky-500 tracking-wider uppercase">Our Products</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Product Catalog</h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">Browse our popular 3D printed products. Every item is crafted with precision and care.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index}
                isVisible={isVisible}
                getProductLink={getProductLink}
                onImageClick={openLightbox}
              />
            ))}
          </div>
        </div>
      </section>

      <ImageLightbox
        images={lightbox.images}
        startIndex={lightbox.startIndex}
        open={lightbox.open}
        onClose={() => setLightbox((s) => ({ ...s, open: false }))}
        model3d={lightbox.model3d}
      />
    </>
  );
};
